import { useEffect } from "react";
import { toast } from "react-toastify";
import type { DataAwareResponse } from "@/types/types";
import errorNotifications from "@/helper/helper";
import { AxiosError } from "axios";
import { useMutation, UseMutationOptions, UseMutationResult } from "@tanstack/react-query";

type CustomMessage = {
  error?: string;
  success?: string;
};

type Props<TData, TVariables> = UseMutationOptions<
  DataAwareResponse<TData>,
  unknown,
  TVariables,
  unknown
> & {
  customMessage?: CustomMessage;
};

export function useMutationWithValidation<TData, TVariables>({
  customMessage,
  ...restOptions
}: Props<TData, TVariables>): UseMutationResult<DataAwareResponse<TData>, unknown, TVariables> {
  const mutation = useMutation<DataAwareResponse<TData>, unknown, TVariables>({
    ...restOptions,
  });

  useEffect(() => {
    const validationItems = mutation?.data?.validation?.items;

    if (validationItems?.length) {
      errorNotifications(validationItems);
    }
  }, [mutation.data]);

  useEffect(() => {
    if (mutation.isSuccess && customMessage?.success && !mutation.data?.validation?.items.length) {
      toast.success(customMessage.success);
    }
  }, [customMessage?.success, mutation.data?.validation?.items.length, mutation.isSuccess]);

  useEffect(() => {
    if (mutation.isError) {
      if (mutation.error instanceof AxiosError) {
        const axiosError = mutation.error as AxiosError;
        const status = axiosError.response?.status;

        if (status === 400) {
          toast.error("Bad Request - Please check your input.");
        } else if (status === 401) {
          toast.error("Unauthorized ");
        } else if (status === 403) {
          toast.error("Forbidden - You do not have permission.");
        } else if (status === 404) {
          toast.error("Resource not found.");
        } else if (status === 500) {
          toast.error("Server error - Please try again later.");
        } else {
          toast.error("An error occurred during the request");
        }
      } else if (mutation.error instanceof Error) {
        toast.error(mutation.error.message);
      } else {
        toast.error("Critical error - please contact support");
      }
    }
  }, [mutation.error, mutation.isError]);

  return mutation;
}
